import { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "./Reveal";

export default function ProjectGallery({ images, name }) {
  const [open, setOpen] = useState(null);
  const step = (d) => setOpen((i) => (i + d + images.length) % images.length);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="project-gallery">
        {images.map((src, i) => (
          <Reveal key={src} delay={(i % 2) * 120} className={i % 5 === 0 ? "md:col-span-2" : ""}>
            <button
              type="button"
              onClick={() => setOpen(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Expand image ${i + 1}`}
              className={`group block w-full overflow-hidden bg-[#EDEBE6] cursor-zoom-in ${
                i % 5 === 0 ? "aspect-[16/9]" : "aspect-[4/5]"
              }`}
            >
              <img
                src={src}
                alt={`${name} — interior view ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-[1.04]"
              />
            </button>
          </Reveal>
        ))}
      </div>

      {open !== null && (
        <div
          className="fixed inset-0 z-[100] bg-[#111111]/95 flex items-center justify-center lightbox-in"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          data-testid="gallery-lightbox"
        >
          <img
            src={images[open]}
            alt={`${name} — interior view ${open + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            data-testid="gallery-lightbox-image"
          />
          <button
            type="button"
            onClick={() => setOpen(null)}
            autoFocus
            data-testid="gallery-lightbox-close"
            aria-label="Close"
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
          >
            <X size={22} strokeWidth={1.25} />
          </button>
          {[-1, 1].map((d) => (
            <button
              key={d}
              type="button"
              onClick={(e) => { e.stopPropagation(); step(d); }}
              data-testid={`gallery-lightbox-${d < 0 ? "prev" : "next"}`}
              aria-label={d < 0 ? "Previous image" : "Next image"}
              className={`absolute top-1/2 -translate-y-1/2 ${d < 0 ? "left-3 md:left-6" : "right-3 md:right-6"} p-3 rounded-full bg-black/40 md:bg-transparent text-white/70 hover:text-white transition-colors`}
            >
              {d < 0 ? <ArrowLeft size={22} strokeWidth={1.25} /> : <ArrowRight size={22} strokeWidth={1.25} />}
            </button>
          ))}
          <span
            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 font-heading text-[11px] tracking-[0.25em]"
            data-testid="gallery-lightbox-counter"
          >
            {String(open + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </span>
        </div>
      )}
    </>
  );
}
